import { useRef } from "react";
import Draggable from "react-draggable";
import {
  ArrowPathIcon,
  CameraIcon,
  ChatBubbleLeftEllipsisIcon,
  ClipboardDocumentCheckIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { deviationStatusLabel } from "./drawingAnnotationUtils";
import {
  parseModuleMeta,
  isInspectableModule,
  observationKindFromModule,
  checklistIdFromModule,
  checklistItemIdFromModule,
  checklistPinStats,
  documentIdFromModule,
  severityColor,
  severityIdFromValue,
} from "./drawingObservationUtils";

const HIT_PADDING = 14;
const MIN_HIT_SIZE = 44;
/**
 * Selectable box for photo, note and checklist pins, in screen pixels.
 * The visible marker is a 28px icon, a 12px stem and a 10px dot.
 * Add about 8px of finger padding beside and above it. The tip stays on the
 * placed point, so the box does not extend below the dot.
 * Sheet size is this divided by zoom, so empty sheet beside the pin stays clickable.
 * zoom 1 → 44×58 sheet px (and screen px).
 * zoom 4 → 11×14.5 sheet px, still 44×58 on screen.
 */
export const PIN_MARKER_SCREEN_HIT = { width: 44, height: 58 };

export const pinMarkerHitSheetSize = (zoom) => {
  const pinScale = zoom > 0 ? 1 / zoom : 1;
  return {
    pinScale,
    width: PIN_MARKER_SCREEN_HIT.width * pinScale,
    height: PIN_MARKER_SCREEN_HIT.height * pinScale,
  };
};
/** Extra hit-area above the shape so the deviation pin stays tappable. */
const DEVIATION_PIN_HEADROOM = 34;
const DEFAULT_DEVIATION_PIN_COLOR = "#e5484d";
/** Mobile double-tap window to open pin detail (matches desktop dblclick). */
const DOUBLE_TAP_MS = 320;
/** Gap between the selected-module toolbar and the module, in screen px at zoom 1. */
const TOOLBAR_OFFSET = 40;

const PinSyncIndicator = ({ status, t }) => {
  if (status === "pending") {
    return (
      <span
        className="mt-0.5 grid h-5 w-5 place-items-center rounded-full bg-[#f4690f] text-white shadow"
        title={t("drawings.observation.sync_pending_badge")}
        aria-label={t("drawings.observation.sync_pending_badge")}
      >
        <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
      </span>
    );
  }
  if (status === "error") {
    return (
      <span
        className="mt-0.5 grid h-5 w-5 place-items-center rounded-full bg-[#e5484d] text-white shadow"
        title={t("drawings.observation.sync_error_badge")}
        aria-label={t("drawings.observation.sync_error_badge")}
      >
        <ExclamationCircleIcon className="h-3.5 w-3.5" />
      </span>
    );
  }
  return null;
};

/** Compact pin badge for deviation shape markers (icon + number, no wide title bar). */
const DeviationPinBadge = ({ module, syncStatus, t }) => {
  const meta = parseModuleMeta(module);
  const severityId =
    severityIdFromValue(module.deviation?.severity ?? meta.severity) || null;
  const pinColor =
    module.color ||
    severityColor(severityId) ||
    DEFAULT_DEVIATION_PIN_COLOR;
  const number =
    module.deviation?.number != null && module.deviation.number !== ""
      ? String(module.deviation.number)
      : null;
  const title =
    module.deviation?.displayTitle ||
    module.deviation?.title ||
    meta.title ||
    t("drawings.observation.type.deviation");
  const statusLabel = module.deviation?.status
    ? deviationStatusLabel(module.deviation.status, t)
    : null;
  const tooltip = statusLabel ? `${title} · ${statusLabel}` : title;
  const numberLabel = number
    ? t("drawings.viewer.deviation_pin_number", { number })
    : t("drawings.viewer.deviation_pin_label");

  return (
    <div
      className="pointer-events-none absolute left-1/2 top-0 z-[2] flex -translate-x-1/2 -translate-y-[calc(100%-6px)] flex-col items-center"
      title={tooltip}
      aria-label={numberLabel}
    >
      <div className="flex items-stretch overflow-hidden rounded-md shadow-[0_4px_14px_rgba(20,23,28,0.22)] ring-1 ring-white/80">
        <span
          className="grid h-7 w-7 flex-none place-items-center text-white"
          style={{ background: pinColor }}
        >
          {syncStatus === "pending" ? (
            <ArrowPathIcon className="h-4 w-4 animate-spin" />
          ) : (
            <ExclamationTriangleIcon className="h-4 w-4" />
          )}
        </span>
        {number ? (
          <span
            className="flex max-w-[3.25rem] items-center bg-[rgba(20,23,28,0.95)] px-1.5 text-[10px] font-bold leading-none text-white tabular-nums"
            title={numberLabel}
          >
            <span className="truncate">{number}</span>
          </span>
        ) : (
          <span
            className="flex items-center bg-[rgba(20,23,28,0.95)] px-1.5 text-[9px] font-bold uppercase leading-none tracking-wide text-white/90"
            title={t("drawings.viewer.deviation_pin_label")}
          >
            {t("drawings.viewer.deviation_pin_short")}
          </span>
        )}
      </div>
      <div className="h-2.5 w-0.5" style={{ background: pinColor }} />
      <div
        className="h-2 w-2 rounded-full border-2 border-white shadow"
        style={{ background: pinColor }}
      />
    </div>
  );
};

const isDeviationShapeModule = (module) =>
  observationKindFromModule(module) === "deviation";

export const DrawingModule = ({
  module,
  pendingChanges,
  sheetSize,
  isSelected,
  checklists = [],
  draggable = true,
  zoom = 1,
  onSelect,
  onMove,
  onConfigure,
  onDelete,
  onOpenDetail,
  onFocusOnDrawing,
  onStartRelocate,
  onRetrySync,
  t,
}) => {
  const lastTapRef = useRef(0);

  if (module.module_type === "pen" || module.module_type === "measure") {
    return null;
  }

  const moduleToRender = pendingChanges[module.id]
    ? { ...module, ...pendingChanges[module.id] }
    : module;

  const moduleWidth = moduleToRender.width || 50;
  const moduleHeight = moduleToRender.height || 50;
  let xPos = moduleToRender.x_position || 0;
  let yPos = moduleToRender.y_position || 0;

  if (sheetSize?.width > 0 && sheetSize?.height > 0) {
    xPos = Math.max(0, Math.min(sheetSize.width - moduleWidth, xPos));
    yPos = Math.max(0, Math.min(sheetSize.height - moduleHeight, yPos));
  }

  const isDeviationShape = isDeviationShapeModule(moduleToRender);
  // Pins/badges are screen-space UI chrome, not to-scale drawing markup — counter-scale
  // them against the canvas zoom so they stay a constant on-screen size (like a map pin)
  // instead of ballooning at high zoom, the way the shape/measure/pen markup should.
  const pinScale = zoom > 0 ? 1 / zoom : 1;
  const pinHit = pinMarkerHitSheetSize(zoom);
  // Placement anchors these at the BOTTOM of the box (pinY = point.y - height, see
  // commitObservationPlacement), so render must bottom-align content too — center-aligning
  // only roughly agreed with that anchor, off by a sheet-space amount that grows with zoom.
  const isPinMarkerKind = ["photo", "note", "checklist"].includes(
    moduleToRender.module_type
  );
  // Extra halo around deviation badges is screen-sized. In sheet pixels it would
  // grow with zoom and block placing another pin next to the badge.
  const deviationPad = isDeviationShape ? HIT_PADDING * pinScale : HIT_PADDING;
  const pinHeadroom =
    isDeviationShape &&
    ["arrow", "circle", "rectangular"].includes(moduleToRender.module_type)
      ? DEVIATION_PIN_HEADROOM * pinScale
      : 0;
  const minHit = isDeviationShape ? MIN_HIT_SIZE * pinScale : MIN_HIT_SIZE;
  const hitWidth = isPinMarkerKind
    ? pinHit.width
    : Math.max(moduleWidth + deviationPad * 2, minHit);
  const hitHeight = isPinMarkerKind
    ? pinHit.height
    : Math.max(moduleHeight + deviationPad * 2 + pinHeadroom, minHit);
  const hitOffsetX = isPinMarkerKind ? 0 : (hitWidth - moduleWidth) / 2;
  // More padding above the shape so the deviation pin stays inside the hit target.
  const hitOffsetY = isPinMarkerKind ? 0 : deviationPad + pinHeadroom;

  const bounds =
    sheetSize?.width > 0 &&
    sheetSize?.height > 0 &&
    moduleWidth > 0 &&
    moduleHeight > 0
      ? {
          left: 0,
          top: 0,
          right: Math.max(0, sheetSize.width - moduleWidth),
          bottom: Math.max(0, sheetSize.height - moduleHeight),
        }
      : "parent";

  const syncStatus =
    moduleToRender._syncStatus === "error"
      ? "error"
      : moduleToRender._syncStatus === "pending" || moduleToRender._localPending
        ? "pending"
        : null;

  let content;
  switch (moduleToRender.module_type) {
    case "arrow":
      content = (
        <div className="relative flex flex-col items-center">
          <div
            className="relative"
            style={{ width: moduleWidth, height: moduleHeight }}
          >
            {isDeviationShape && (
              <div
                className="pointer-events-none absolute inset-0"
                style={{ transform: `scale(${pinScale})`, transformOrigin: "50% 0%" }}
              >
                <DeviationPinBadge
                  module={moduleToRender}
                  syncStatus={syncStatus}
                  t={t}
                />
              </div>
            )}
            <svg
              width={moduleWidth}
              height={moduleHeight}
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ transform: "rotate(-45deg)" }}
            >
              <path
                d="M12 2L2 12L12 22M2 12H22"
                stroke={
                  moduleToRender.color ||
                  (isDeviationShape ? DEFAULT_DEVIATION_PIN_COLOR : "#14171c")
                }
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill={
                  isDeviationShape
                    ? "none"
                    : moduleToRender.filled
                      ? moduleToRender.color || "#14171c"
                      : "none"
                }
              />
            </svg>
          </div>
          {!isDeviationShape && <PinSyncIndicator status={syncStatus} t={t} />}
          {isDeviationShape && syncStatus === "error" && (
            <PinSyncIndicator status="error" t={t} />
          )}
        </div>
      );
      break;
    case "circle": {
      // Deviation markers must stay outline-only so the drawing underneath remains visible.
      const circleFilled = isDeviationShape
        ? false
        : Boolean(moduleToRender.filled);
      const circleStroke =
        moduleToRender.color ||
        (isDeviationShape ? DEFAULT_DEVIATION_PIN_COLOR : "#14171c");
      content = (
        <div className="relative flex flex-col items-center">
          <div
            className="relative"
            style={{ width: moduleWidth, height: moduleHeight }}
          >
            {isDeviationShape && (
              <div
                className="pointer-events-none absolute inset-0"
                style={{ transform: `scale(${pinScale})`, transformOrigin: "50% 0%" }}
              >
                <DeviationPinBadge
                  module={moduleToRender}
                  syncStatus={syncStatus}
                  t={t}
                />
              </div>
            )}
            <div
              style={{
                width: moduleWidth,
                height: moduleHeight,
                borderRadius: "50%",
                border: `2px solid ${circleStroke}`,
                backgroundColor: circleFilled
                  ? moduleToRender.color || "#14171c"
                  : "transparent",
              }}
            />
            {!isDeviationShape && syncStatus === "pending" && (
              <span className="absolute inset-0 grid place-items-center">
                <span className="grid h-6 w-6 place-items-center rounded-full bg-white/90 text-[#f4690f] shadow">
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                </span>
              </span>
            )}
          </div>
          {syncStatus === "error" && <PinSyncIndicator status="error" t={t} />}
        </div>
      );
      break;
    }
    case "rectangular": {
      const rectStroke =
        moduleToRender.color ||
        (isDeviationShape ? DEFAULT_DEVIATION_PIN_COLOR : "#14171c");
      content = (
        <div className="relative flex flex-col items-center">
          <div
            className="relative"
            style={{ width: moduleWidth, height: moduleHeight }}
          >
            {isDeviationShape && (
              <div
                className="pointer-events-none absolute inset-0"
                style={{ transform: `scale(${pinScale})`, transformOrigin: "50% 0%" }}
              >
                <DeviationPinBadge
                  module={moduleToRender}
                  syncStatus={syncStatus}
                  t={t}
                />
              </div>
            )}
            <div
              style={{
                width: moduleWidth,
                height: moduleHeight,
                border: `2px solid ${rectStroke}`,
                backgroundColor:
                  isDeviationShape
                    ? "transparent"
                    : moduleToRender.filled
                      ? moduleToRender.color || "#14171c"
                      : "transparent",
              }}
            />
            {!isDeviationShape && syncStatus === "pending" && (
              <span className="absolute inset-0 grid place-items-center">
                <span className="grid h-6 w-6 place-items-center rounded-full bg-white/90 text-[#f4690f] shadow">
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                </span>
              </span>
            )}
          </div>
          {syncStatus === "error" && <PinSyncIndicator status="error" t={t} />}
        </div>
      );
      break;
    }
    case "photo":
    case "note":
    case "checklist": {
      const meta = parseModuleMeta(moduleToRender);
      const kind = observationKindFromModule(moduleToRender) || moduleToRender.module_type;
      const PinIcon =
        kind === "photo"
          ? CameraIcon
          : kind === "checklist"
            ? ClipboardDocumentCheckIcon
            : ChatBubbleLeftEllipsisIcon;
      const pinColor = moduleToRender.color || "#2b6ef0";
      const stats =
        kind === "checklist" || (kind === "photo" && checklistIdFromModule(moduleToRender))
          ? checklistPinStats(moduleToRender, checklists)
          : null;
      // Checklist pins: icon + compact count chips only (no meta.title white bar — that was up to 180px wide).
      const showTitleBar = kind !== "checklist" && Boolean(meta.title);
      const pinTitle =
        kind === "checklist"
          ? stats?.checklist?.displayTitle ||
            stats?.checklist?.title ||
            meta.title ||
            t("drawings.module.checklist")
          : meta.title || t(`drawings.module.${kind || "note"}`);

      content = (
        <div className="flex flex-col items-center" title={pinTitle}>
          <div
            className={`relative flex items-stretch overflow-hidden rounded-md shadow-[0_4px_14px_rgba(20,23,28,0.18)] ${
              kind === "checklist" ? "" : "max-w-[100px]"
            }`}
          >
            <span
              className="grid h-7 w-7 flex-none place-items-center text-white"
              style={{ background: pinColor }}
            >
              {syncStatus === "pending" ? (
                <ArrowPathIcon className="h-4 w-4 animate-spin" />
              ) : (
                <PinIcon className="h-4 w-4" />
              )}
            </span>
            {kind === "checklist" && stats ? (
              <span className="flex items-center gap-0.5 bg-[rgba(20,23,28,0.95)] px-1 py-0.5">
                {stats.completed ? (
                  <span
                    className="rounded px-1 text-[9px] font-bold leading-none text-[#7dffa3]"
                    title={t("drawings.viewer.checklist_pin_completed")}
                  >
                    ✓
                  </span>
                ) : null}
                <span
                  className="rounded bg-[#0b7a3b] px-1 text-[9px] font-bold leading-none text-white tabular-nums"
                  title={t("drawings.viewer.checklist_pin_approved_count", {
                    count: stats.approved,
                  })}
                >
                  {stats.approved}
                </span>
                <span
                  className={`rounded px-1 text-[9px] font-bold leading-none tabular-nums ${
                    stats.deviations > 0
                      ? "bg-[#e5484d] text-white"
                      : "bg-white/15 text-white/80"
                  }`}
                  title={t("drawings.viewer.checklist_pin_deviation_count", {
                    count: stats.deviations,
                  })}
                >
                  {stats.deviations}
                </span>
              </span>
            ) : null}
            {showTitleBar ? (
              <span className="flex min-w-0 max-w-[64px] items-center bg-white px-1.5 text-[10px] font-semibold text-[#14171c]">
                <span className="truncate">{meta.title}</span>
              </span>
            ) : null}
          </div>
          {syncStatus === "error" && <PinSyncIndicator status="error" t={t} />}
          <div className="h-3 w-0.5" style={{ background: pinColor }} />
          <div
            className="h-2.5 w-2.5 rounded-full border-2 border-white shadow"
            style={{ background: pinColor }}
          />
        </div>
      );
      break;
    }
    default:
      return null;
  }

  const selectFromPointer = (event) => {
    event.stopPropagation();
    // Single click / tap = select + highlight only.
    // Open detail / fill-out via double-click / double-tap, eye button, or panel.
    onSelect(module);

    // Touch devices often don't fire dblclick — detect double-tap here.
    if (event.pointerType === "touch") {
      const now = Date.now();
      if (now - lastTapRef.current < DOUBLE_TAP_MS) {
        lastTapRef.current = 0;
        onOpenDetail?.(module);
        return;
      }
      lastTapRef.current = now;
    }
  };

  const openFromDoubleClick = (event) => {
    event.stopPropagation();
    onOpenDetail?.(module);
  };

  const pinVisual = (
    <div
      className={`rounded-sm transition-shadow ${
        isSelected
          ? "shadow-[0_0_0_2px_#2b6ef0,0_0_0_5px_rgba(43,110,240,0.35)]"
          : ""
      }`}
      style={
        isPinMarkerKind
          ? {
              // Bottom-center scale keeps the pin tip on the placed sheet point.
              transform: `scale(${pinScale})`,
              transformOrigin: "50% 100%",
            }
          : undefined
      }
    >
      {content}
    </div>
  );

  const body = (
    <div
      {...(isPinMarkerKind ? {} : { "data-module-hit": true })}
      data-module-id={module.id}
      style={{
        position: "absolute",
        left: 0,
        top: 0,
        width: isPinMarkerKind ? moduleWidth : hitWidth,
        height: isPinMarkerKind ? moduleHeight : hitHeight,
        marginLeft: isPinMarkerKind ? 0 : -hitOffsetX,
        marginTop: isPinMarkerKind ? 0 : -hitOffsetY,
        cursor: draggable ? "move" : "default",
        zIndex: isSelected ? 1000 : 999,
        touchAction: draggable ? "none" : "auto",
        // The stored module box stays the drag anchor. It must not catch clicks:
        // at high zoom that box is many times larger than the pin on screen.
        ...(isPinMarkerKind ? { pointerEvents: "none" } : null),
        ...(pinHeadroom > 0
          ? { paddingTop: deviationPad + pinHeadroom }
          : null),
      }}
      onPointerDown={isPinMarkerKind ? undefined : selectFromPointer}
      onDoubleClick={isPinMarkerKind ? undefined : openFromDoubleClick}
      className={`group flex justify-center ${
        pinHeadroom > 0
          ? "items-start"
          : isPinMarkerKind
            ? "items-end"
            : "items-center"
      }`}
    >
      {isPinMarkerKind ? (
        <div
          data-module-hit
          data-pin-hit
          onPointerDown={selectFromPointer}
          onDoubleClick={openFromDoubleClick}
          style={{
            position: "absolute",
            left: "50%",
            bottom: 0,
            width: pinHit.width,
            height: pinHit.height,
            transform: "translateX(-50%)",
            pointerEvents: "auto",
            cursor: draggable ? "move" : "default",
          }}
        >
          <div
            style={{
              position: "absolute",
              left: "50%",
              bottom: 0,
              transform: "translateX(-50%)",
            }}
          >
            {pinVisual}
          </div>
        </div>
      ) : (
        pinVisual
      )}

      {isSelected && (
        <div
          data-module-hit
          className="absolute left-1/2 flex gap-1 rounded-[10px] border border-[#e5e8ec] bg-white p-1 shadow-[0_8px_20px_rgba(15,23,42,0.12)]"
          style={{
            // Toolbar is UI chrome, not to-scale content — counter-scale it (and its
            // offset above the module) against zoom the same way the pin visuals are,
            // so it stays a constant size and gap instead of ballooning/drifting at zoom.
            // Pin markers anchor the bar just above the visible pin, not the sheet box.
            ...(isPinMarkerKind
              ? { bottom: pinHit.height + 8 * pinScale, top: "auto" }
              : { top: -TOOLBAR_OFFSET * pinScale }),
            pointerEvents: "auto",
            transform: `translateX(-50%) scale(${pinScale})`,
            transformOrigin: isPinMarkerKind ? "50% 100%" : "50% 100%",
          }}
          onPointerDown={(event) => event.stopPropagation()}
        >
          {isInspectableModule(module) && (
            <button
              type="button"
              onPointerDown={(event) => event.stopPropagation()}
              onClick={(event) => {
                event.stopPropagation();
                onOpenDetail?.(module);
              }}
              className="grid h-7 w-7 place-items-center rounded-[7px] text-[#59616d] hover:bg-[#eef0f3]"
              title={t("drawings.observation.view_details")}
            >
              <EyeIcon className="h-4 w-4" />
            </button>
          )}
          {isInspectableModule(module) && onStartRelocate && !module._localPending && (
            <button
              type="button"
              onPointerDown={(event) => event.stopPropagation()}
              onClick={(event) => {
                event.stopPropagation();
                onStartRelocate(module);
              }}
              className="grid h-7 w-7 place-items-center rounded-[7px] text-[#2b6ef0] hover:bg-[#eaf1fe]"
              title={t("drawings.observation.relocate")}
            >
              <ArrowPathIcon className="h-4 w-4" />
            </button>
          )}
          {module._syncStatus === "error" && onRetrySync && (
            <button
              type="button"
              onPointerDown={(event) => event.stopPropagation()}
              onClick={(event) => {
                event.stopPropagation();
                onRetrySync(module);
              }}
              className="rounded-[7px] px-1.5 text-[10px] font-bold uppercase text-[#e5484d] hover:bg-red-50"
              title={t("drawings.observation.retry_save")}
            >
              {t("drawings.observation.retry_save")}
            </button>
          )}
          {module.module_type === "rectangular" && draggable && (
            <button
              type="button"
              onPointerDown={(event) => event.stopPropagation()}
              onClick={(event) => {
                event.stopPropagation();
                onConfigure(module);
              }}
              className="grid h-7 w-7 place-items-center rounded-[7px] text-[#2b6ef0] hover:bg-[#eaf1fe]"
              title={t("drawings.module.color")}
            >
              <PencilIcon className="h-4 w-4" />
            </button>
          )}
          <button
            type="button"
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.stopPropagation();
              onDelete(module.id);
            }}
            className="grid h-7 w-7 place-items-center rounded-[7px] text-red-600 hover:bg-red-50"
            title={t("drawings.module.delete")}
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );

  if (!draggable) {
    return (
      <div style={{ position: "absolute", left: xPos, top: yPos }}>{body}</div>
    );
  }

  return (
    <Draggable
      position={{ x: xPos, y: yPos }}
      onStop={(event, data) => {
        event.stopPropagation();
        onMove(module.id, { x: data.x, y: data.y });
      }}
      onStart={(event) => {
        event.stopPropagation();
        onSelect(module);
      }}
      bounds={bounds}
      disabled={!draggable}
    >
      {body}
    </Draggable>
  );
};
